import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { styled } from 'styled-components';
import { Accordion } from 'react-bootstrap';
import Tooltip from 'react-bootstrap/Tooltip';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { MdModeEdit } from 'react-icons/md';
import { MdDelete } from 'react-icons/md';
import { GrRevert } from 'react-icons/gr';
import DeleteConfirmationModal from '../components/shared/DeleteConfirmationModal';
import { FaMagnifyingGlass } from 'react-icons/fa6';
import StockMovementDetailsModal from '../components/shared/StockMovementDetailsModal';
import MovementReturnModal from '../components/MovementReturnModal';
import EditMovementReturnModal from '../components/EditMovementReturnModal';

import { useAppContext } from '../contextApi/context';
import {
    getWorkOrder,
    getMaintenanceTypes,
    editWorkOrder,
    approveWorkOrder,
    startMaintenanceWorkOrder,
    discardWorkOrder,
    finalizeWorkOrder,
    createInternalService,
    editInternalService,
    deleteInternalService,
    createExternalService,
    editExternalService,
    deleteExternalService,
} from '../services/OrdersService';
import { searchUsersByName } from '../services/UsersService';
import { getVehicle } from '../services/VehiclesService';
import { ORDER_STATUS, getOrderStatusConfig, getAvailableStatusTransitions } from '../constants/orderStatus';
import { getClientTypeLabel } from '../constants/orderClientTypes';
import { checkPermission } from '../utils/permissions';
import { formatDateAndTime, formatDateOnly } from '../utils/dates';
import ChangeStatusConfirmationModal from '../components/shared/ChangeStatusConfirmationModal';
import InternalServiceModal from '../components/shared/InternalServiceModal';
import ExternalServiceModal from '../components/shared/ExternalServiceModal';
import CreateIssueMovementModal from '../components/shared/CreateIssueMovementModal';
import { AddButton, TableActionButton } from '../styles/StyledComponents';
import { formatCurrency, formatHours } from '../utils/numbers';
import { getIssueMovements, getMovementDetails } from '../services/StockMovementService';

const EditOrder = () => {
    // Context/Hooks
    const location = useLocation();
    const navigate = useNavigate();
    const { loggedUser } = useAppContext();

    // Obtener orderId desde el state de navegación
    const orderId = location.state?.orderId;

    // State - Mensajes
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [showOrderEdited, setShowOrderEdited] = useState(false);
    const [showStatusChanged, setShowStatusChanged] = useState(false);
    const [showErrorDuplicatedData, setShowErrorDuplicatedData] = useState(false);
    const [errorDuplicatedData, setErrorDuplicatedData] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [isPartialSuccess, setIsPartialSuccess] = useState(false);

    // State - Orden
    const [currentOrder, setCurrentOrder] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);

    // State - Vehículo (para obtener odometer cuando venga null)
    const [vehicleData, setVehicleData] = useState(null);
    const [isLoadingVehicle, setIsLoadingVehicle] = useState(false);

    // State - Usuario (Solicitante)
    const [availableUsers, setAvailableUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [showUserError, setShowUserError] = useState(false);

    // State - Tipos de Mantenimiento
    const [maintenanceTypes, setMaintenanceTypes] = useState([]);
    const [selectedMaintenanceTypeId, setSelectedMaintenanceTypeId] = useState(null);
    const [showMaintenanceTypeError, setShowMaintenanceTypeError] = useState(false);

    // State - Instrucciones
    const [instructionsValue, setInstructionsValue] = useState('');

    const [odometerValue, setOdometerValue] = useState('');
    const [showOdometerError, setShowOdometerError] = useState(false);
    const [showOdometerLowerError, setShowOdometerLowerError] = useState(false);

    // State - Cambio de estado
    const [isChangingStatus, setIsChangingStatus] = useState(false);

    // State - Modal de confirmación de cambio de estado
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [pendingStatusChange, setPendingStatusChange] = useState(null);

    // State - Servicios Internos
    const [internalServices, setInternalServices] = useState([]);
    const [showInternalServiceModal, setShowInternalServiceModal] = useState(false);
    const [editingInternalService, setEditingInternalService] = useState(null);
    const [isSubmittingInternalService, setIsSubmittingInternalService] = useState(false);
    const [showInternalServiceCreated, setShowInternalServiceCreated] = useState(false);
    const [showInternalServiceError, setShowInternalServiceError] = useState(false);
    const [showInternalServiceEdited, setShowInternalServiceEdited] = useState(false);
    const [showInternalServiceDeleted, setShowInternalServiceDeleted] = useState(false);
    const [showDeleteInternalServiceModal, setShowDeleteInternalServiceModal] = useState(false);
    const [deletingInternalServiceId, setDeletingInternalServiceId] = useState(null);

    // State - Servicios Externos
    const [externalServices, setExternalServices] = useState([]);
    const [showExternalServiceModal, setShowExternalServiceModal] = useState(false);
    const [editingExternalService, setEditingExternalService] = useState(null);
    const [isSubmittingExternalService, setIsSubmittingExternalService] = useState(false);
    const [showExternalServiceCreated, setShowExternalServiceCreated] = useState(false);
    const [showExternalServiceEdited, setShowExternalServiceEdited] = useState(false);
    const [showExternalServiceDeleted, setShowExternalServiceDeleted] = useState(false);
    const [showDeleteExternalServiceModal, setShowDeleteExternalServiceModal] = useState(false);
    const [deletingExternalServiceId, setDeletingExternalServiceId] = useState(null);

    // Issues
    const [issueMovements, setIssueMovements] = useState([]);
    const [isLoadingIssues, setIsLoadingIssues] = useState(false);
    const [showIssueDetailsModal, setShowIssueDetailsModal] = useState(false);
    const [selectedIssueId, setSelectedIssueId] = useState(null);
    const [showCreateIssueMovementModal, setShowCreateIssueMovementModal] = useState(false);
    const [showIssueMovementCreated, setShowIssueMovementCreated] = useState(false);

    // State - Modales de devolución de despachos
    const [showReturnModal, setShowReturnModal] = useState(false);
    const [showEditReturnModal, setShowEditReturnModal] = useState(false);
    const [selectedReturnId, setSelectedReturnId] = useState(null);
    const [showMovementEdited, setShowMovementEdited] = useState(false);

    // State - Detalles de despachos
    const [issueMovementDetails, setIssueMovementDetails] = useState({});
    const [loadingIssueDetails, setLoadingIssueDetails] = useState({});

    // State - Acordeón
    const [activeKey, setActiveKey] = useState('0');

    // Variables para controlar qué campos son editables según el estado
    const isInCreatedStatus = currentOrder?.status === ORDER_STATUS.CREATED.value;
    const isInApprovedStatus = currentOrder?.status === ORDER_STATUS.APPROVED.value;

    // Campos editables según el estado
    const canEditAllFields = isInCreatedStatus;
    const canEditOnlyOdometer = isInApprovedStatus;
    const canEditOdometer = canEditAllFields || canEditOnlyOdometer;

    // Timer para búsquedas
    const [timer, setTimer] = useState(null);

    // Hooks para formulario de datos principales
    const { handleSubmit: handleSubmitMain, reset: resetMain } = useForm({
        defaultValues: {
            vehiclePlate: '',
            status: '',
            orderClientType: '',
        },
    });

    // Función para obtener datos del vehículo cuando el odometer sea null
    const fetchVehicleData = useCallback(
        async (vehicleId) => {
            if (!vehicleId || !loggedUser?.token) {
                return;
            }

            setIsLoadingVehicle(true);

            try {
                const response = await getVehicle(loggedUser.token, vehicleId);

                if (response) {
                    setVehicleData(response);
                }
            } catch (error) {
                console.error('Error al cargar datos del vehículo:', error);
            } finally {
                setIsLoadingVehicle(false);
            }
        },
        [loggedUser.token]
    );

    // Cargar tipos de mantenimiento desde la API
    const fetchMaintenanceTypes = useCallback(async () => {
        if (!loggedUser?.token) return;

        try {
            const response = await getMaintenanceTypes(loggedUser.token);

            if (response && Array.isArray(response)) {
                setMaintenanceTypes(response);
            } else {
                console.error('Formato inesperado en tipos de mantenimiento:', response);
                setMaintenanceTypes([]);
            }
        } catch (error) {
            console.error('Error al cargar tipos de mantenimiento:', error);
            setMaintenanceTypes([]);
        }
    }, [loggedUser]);

    // API Fetch Actions
    const fetchOrderData = useCallback(async () => {
        if (!orderId || !loggedUser?.token) {
            setLoadError('No se pudo cargar la orden. Información de sesión no válida.');
            setIsLoading(false);
            return null;
        }

        setIsLoading(true);
        setLoadError(null);

        try {
            const response = await getWorkOrder(loggedUser.token, orderId);

            if (response.success) {
                const orderData = response.data;
                setCurrentOrder(orderData);

                // Prellenar formulario principal con datos de la orden
                resetMain({
                    vehiclePlate: orderData.vehiclePlate || '',
                    status: orderData.status || '',
                    orderClientType: orderData.orderClientType || '',
                });

                // Establecer valor de instrucciones
                setInstructionsValue(orderData.instructions || '');

                // Establecer valor del odómetro
                setOdometerValue(orderData.odometer != null ? orderData.odometer.toString() : '');

                // Si el odometer es null, cargar datos del vehículo para obtener su odometer real
                if ((orderData.odometer === null || orderData.odometer === undefined || orderData.odometer === '') && orderData.vehicleId) {
                    await fetchVehicleData(orderData.vehicleId);
                }

                // Establecer tipo de mantenimiento seleccionado
                if (orderData.maintenanceTypeId) {
                    setSelectedMaintenanceTypeId(orderData.maintenanceTypeId);
                }

                // Inicializar usuario seleccionado desde la orden
                if (orderData.requesterId && orderData.requesterUser) {
                    try {
                        const userResponse = await searchUsersByName(loggedUser.token, orderData.requesterUser);

                        if (userResponse && Array.isArray(userResponse) && userResponse.length > 0) {
                            const matchedUser = userResponse.find((user) => user.id === orderData.requesterId);

                            if (matchedUser) {
                                const formattedUser = {
                                    id: matchedUser.id,
                                    label: `${matchedUser.firstName} ${matchedUser.lastName} (${matchedUser.username})`,
                                };

                                setSelectedUserId(matchedUser.id);
                                setAvailableUsers([formattedUser]);
                            } else {
                                const fallbackUser = {
                                    id: orderData.requesterId,
                                    label: orderData.requesterUser,
                                };
                                setSelectedUserId(orderData.requesterId);
                                setAvailableUsers([fallbackUser]);
                            }
                        } else {
                            const fallbackUser = {
                                id: orderData.requesterId,
                                label: orderData.requesterUser,
                            };
                            setSelectedUserId(orderData.requesterId);
                            setAvailableUsers([fallbackUser]);
                        }
                    } catch (error) {
                        console.error('Error al buscar usuario inicial:', error);
                        const fallbackUser = {
                            id: orderData.requesterId,
                            label: orderData.requesterUser,
                        };
                        setSelectedUserId(orderData.requesterId);
                        setAvailableUsers([fallbackUser]);
                    }
                }

                // Cargar servicios internos existentes
                if (orderData.performedWorks && orderData.performedWorks.length > 0) {
                    setInternalServices(orderData.performedWorks);
                } else {
                    setInternalServices([]);
                }

                // Cargar servicios externos existentes
                if (orderData.externalWorks && orderData.externalWorks.length > 0) {
                    setExternalServices(orderData.externalWorks);
                } else {
                    setExternalServices([]);
                }

                setIsLoading(false);
                return orderData;
            } else {
                setLoadError(response.error || 'Error al cargar la orden de trabajo');
                setIsLoading(false);
                return null;
            }
        } catch (error) {
            console.error('Error al obtener orden:', error);
            setLoadError('Error inesperado al cargar la orden de trabajo');
            setIsLoading(false);
            return null;
        }
    }, [orderId, loggedUser, resetMain, fetchVehicleData]);

    /**
     * Obtiene los detalles específicos de un despacho (artículos, cantidades, etc.)
     */
    const fetchIssueMovementDetails = useCallback(
        async (movementId) => {
            if (!movementId || !loggedUser?.token) return;

            // Evitar cargar si ya está cargando o ya se cargó
            if (loadingIssueDetails[movementId] || issueMovementDetails[movementId]) return;

            setLoadingIssueDetails((prev) => ({ ...prev, [movementId]: true }));

            try {
                const response = await getMovementDetails(loggedUser.token, movementId);

                if (response) {
                    // Si hay devolución, cargar también esos detalles
                    if (response.returnMovement !== null) {
                        try {
                            const returnResponse = await getMovementDetails(loggedUser.token, response.returnMovement);
                            setIssueMovementDetails((prev) => ({
                                ...prev,
                                [movementId]: {
                                    ...response,
                                    returnMovementDetails: returnResponse,
                                },
                            }));
                        } catch (returnError) {
                            console.error(`Error al cargar detalles de devolución ${response.returnMovement}:`, returnError);
                            // Guardar sin detalles de devolución si falla
                            setIssueMovementDetails((prev) => ({
                                ...prev,
                                [movementId]: response,
                            }));
                        }
                    } else {
                        setIssueMovementDetails((prev) => ({
                            ...prev,
                            [movementId]: response,
                        }));
                    }
                }
            } catch (error) {
                console.error(`Error al cargar detalles del despacho ${movementId}:`, error);
            } finally {
                setLoadingIssueDetails((prev) => ({ ...prev, [movementId]: false }));
            }
        },
        [loggedUser.token, loadingIssueDetails, issueMovementDetails]
    );

    /**
     * Obtiene la lista de despachos asociados a la orden de trabajo
     */
    const fetchIssueMovements = useCallback(async () => {
        if (!orderId) return;

        setIsLoadingIssues(true);

        try {
            const filters = [
                {
                    columnName: 'workOrderId',
                    operation: 'equal',
                    value: [orderId],
                },
            ];

            const response = await getIssueMovements(loggedUser.token, 0, 100, 'eventDate,asc', filters);

            if (response && response.content) {
                setIssueMovements(response.content);
            } else {
                setIssueMovements([]);
            }
        } catch (error) {
            console.error('Error al cargar despachos:', error);
            setIssueMovements([]);
        } finally {
            setIsLoadingIssues(false);
        }
    }, [orderId, loggedUser.token]);

    // Effect separado para cargar los detalles del primer despacho
    useEffect(() => {
        if (issueMovements.length > 0 && !isLoadingIssues) {
            const firstIssueId = issueMovements[0].id;
            // Verificar si ya se cargaron los detalles del primer despacho
            if (!issueMovementDetails[firstIssueId] && !loadingIssueDetails[firstIssueId]) {
                fetchIssueMovementDetails(firstIssueId);
            }
        }
    }, [issueMovements, isLoadingIssues, issueMovementDetails, loadingIssueDetails, fetchIssueMovementDetails]);

    useEffect(() => {
        if (orderId) {
            fetchOrderData();
            fetchIssueMovements();
        }
    }, [orderId, fetchOrderData, fetchIssueMovements]);

    // Cargar tipos de mantenimiento al montar el componente
    useEffect(() => {
        fetchMaintenanceTypes();
    }, [fetchMaintenanceTypes]);

    // Cargar datos de la orden al montar el componente
    useEffect(() => {
        fetchOrderData();
    }, [fetchOrderData]);

    // Verificar mensaje de creación exitosa
    useEffect(() => {
        if (location.state?.showSuccessMessage && location.state?.justCreated) {
            setShowSuccessMessage(true);

            if (location.state?.successMessage) {
                setSuccessMessage(location.state.successMessage);
            }

            if (location.state?.isPartialSuccess) {
                setIsPartialSuccess(true);
            }

            const timer = setTimeout(() => {
                setShowSuccessMessage(false);
                setSuccessMessage('');
                setIsPartialSuccess(false);
            }, 5000);

            window.history.replaceState({}, document.title);

            return () => clearTimeout(timer);
        }
    }, [location.state]);

    // Hook unificado para auto-ocultar todos los mensajes después de 5 segundos
    useEffect(() => {
        const messages = [
            { state: showErrorDuplicatedData, setState: setShowErrorDuplicatedData, resetError: true },
            { state: showUserError, setState: setShowUserError },
            { state: showMaintenanceTypeError, setState: setShowMaintenanceTypeError },
            { state: showStatusChanged, setState: setShowStatusChanged },
            { state: showOrderEdited, setState: setShowOrderEdited },
            { state: showInternalServiceCreated, setState: setShowInternalServiceCreated },
            { state: showInternalServiceEdited, setState: setShowInternalServiceEdited },
            { state: showInternalServiceError, setState: setShowInternalServiceError },
            { state: showInternalServiceDeleted, setState: setShowInternalServiceDeleted },
            { state: showExternalServiceCreated, setState: setShowExternalServiceCreated },
            { state: showExternalServiceEdited, setState: setShowExternalServiceEdited },
            { state: showExternalServiceDeleted, setState: setShowExternalServiceDeleted },
            { state: showIssueMovementCreated, setState: setShowIssueMovementCreated },
            { state: showOdometerLowerError, setState: setShowOdometerLowerError },
            { state: showMovementEdited, setState: setShowMovementEdited },
        ];

        const timers = messages
            .filter((msg) => msg.state)
            .map((msg) => {
                return setTimeout(() => {
                    msg.setState(false);
                    if (msg.resetError) {
                        setErrorDuplicatedData('');
                    }
                }, 5000);
            });

        return () => {
            timers.forEach((timer) => clearTimeout(timer));
        };
    }, [
        showErrorDuplicatedData,
        showUserError,
        showMaintenanceTypeError,
        showStatusChanged,
        showOrderEdited,
        showInternalServiceCreated,
        showInternalServiceEdited,
        showInternalServiceError,
        showInternalServiceDeleted,
        showExternalServiceCreated,
        showExternalServiceEdited,
        showExternalServiceDeleted,
        showIssueMovementCreated,
        showOdometerLowerError,
        showMovementEdited,
    ]);

    // Manejo de selección de usuario (Solicitante)
    const handleSelectUser = (event, newValue) => {
        if (newValue != null) {
            setShowUserError(false);
            setSelectedUserId(newValue.id);
        } else {
            setSelectedUserId(null);
        }
    };

    const handleShowIssueDetails = (id) => {
        setSelectedIssueId(id);
        setShowIssueDetailsModal(true);
    };

    const handleCloseIssueDetailsModal = () => {
        setSelectedIssueId(null);
        setShowIssueDetailsModal(false);
    };

    // Handlers para modales de devolución
    const handleShowReturnModal = (id) => {
        setSelectedIssueId(id);
        setShowReturnModal(true);
    };

    const handleShowEditReturnModal = (returnId) => {
        setSelectedReturnId(returnId);
        setShowEditReturnModal(true);
    };

    const handleCloseReturnModal = () => {
        setSelectedIssueId(null);
        setShowReturnModal(false);
    };

    const handleCloseEditReturnModal = () => {
        setSelectedReturnId(null);
        setShowEditReturnModal(false);
    };

    const handleReturn = () => {
        setShowReturnModal(false);
        setShowMovementEdited(true);

        // Limpiar los detalles cargados para forzar recarga
        setIssueMovementDetails({});
        setLoadingIssueDetails({});

        // Recargar los despachos para reflejar la devolución
        if (orderId) {
            fetchIssueMovements();
        }

        // Scroll hacia arriba para mostrar el mensaje
        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
    };

    const handleEditReturn = () => {
        setShowEditReturnModal(false);
        setShowMovementEdited(true);

        // Limpiar los detalles cargados para forzar recarga
        setIssueMovementDetails({});
        setLoadingIssueDetails({});

        // Recargar los despachos para reflejar los cambios
        if (orderId) {
            fetchIssueMovements();
        }

        // Scroll hacia arriba para mostrar el mensaje
        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
    };

    /**
     * Handler ejecutado cuando se crea exitosamente un despacho desde el modal.
     */
    const handleIssueMovementCreated = useCallback(() => {
        setShowIssueMovementCreated(true);

        if (orderId) {
            fetchIssueMovements();
        }

        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        });

        setTimeout(() => {
            setShowIssueMovementCreated(false);
        }, 5000);
    }, [orderId, fetchIssueMovements]);

    // Búsqueda de usuarios con debounce
    const handleSearchUsers = (e, newValue, reason) => {
        clearTimeout(timer);

        const newTimer = setTimeout(async () => {
            if (reason === 'clear') {
                if (currentOrder?.requesterId) {
                    setAvailableUsers([
                        {
                            id: currentOrder.requesterId,
                            label: currentOrder.requesterUser,
                        },
                    ]);
                } else {
                    setAvailableUsers([]);
                }
                setSelectedUserId(null);
            }

            if (newValue !== '') {
                if (reason === 'input') {
                    try {
                        const response = await searchUsersByName(loggedUser.token, newValue);

                        if (response && Array.isArray(response) && response.length > 0) {
                            setAvailableUsers(
                                response.map((user) => ({
                                    id: user.id,
                                    label: `${user.firstName} ${user.lastName} (${user.username})`,
                                }))
                            );
                        } else {
                            setAvailableUsers([]);
                        }
                    } catch (error) {
                        console.error('Error al buscar usuarios:', error);
                        setAvailableUsers([]);
                    }
                }
            }
        }, 500);

        setTimer(newTimer);
    };

    // Manejo de selección de tipo de mantenimiento
    const handleSelectMaintenanceType = (event, newValue) => {
        if (newValue != null) {
            setShowMaintenanceTypeError(false);
            setSelectedMaintenanceTypeId(newValue.id);
        } else {
            setSelectedMaintenanceTypeId(null);
        }
    };

    // Manejo de cambio en instrucciones
    const handleInstructionsChange = (event) => {
        setInstructionsValue(event.target.value);
    };

    const handleOdometerChange = (event) => {
        const value = event.target.value;
        const numberRegex = /^\d*\.?\d*$/;

        if (numberRegex.test(value) || value === '') {
            setOdometerValue(value);
            setShowOdometerError(false);

            // Verificar contra el odometer de la orden actual o del vehículo como fallback
            const currentOdometerValue = currentOrder?.odometer || vehicleData?.odometer;

            if (value && currentOdometerValue) {
                const newValue = Number(value);
                const currentValue = Number(currentOdometerValue);

                if (newValue < currentValue) {
                    setShowOdometerLowerError(true);
                } else {
                    setShowOdometerLowerError(false);
                }
            } else {
                setShowOdometerLowerError(false);
            }
        } else {
            setShowOdometerError(true);
        }
    };

    // Limpiar todos los mensajes
    const clearMessages = () => {
        setShowOrderEdited(false);
        setShowStatusChanged(false);
        setShowErrorDuplicatedData(false);
        setErrorDuplicatedData('');
        setShowUserError(false);
        setShowMaintenanceTypeError(false);
        setShowInternalServiceCreated(false);
        setShowInternalServiceEdited(false);
        setShowInternalServiceError(false);
        setShowInternalServiceDeleted(false);
        setShowExternalServiceCreated(false);
        setShowExternalServiceEdited(false);
        setShowExternalServiceDeleted(false);
        setShowOdometerError(false);
        setShowOdometerLowerError(false);
    };

    // Handler para editar datos principales
    const handleEditMainData = async () => {
        clearMessages();

        if (canEditAllFields) {
            if (!selectedUserId) {
                setShowUserError(true);
                return;
            }

            if (!selectedMaintenanceTypeId) {
                setShowMaintenanceTypeError(true);
                return;
            }

            if (instructionsValue.length > 500) {
                setErrorDuplicatedData('Las instrucciones no pueden exceder los 500 caracteres');
                setShowErrorDuplicatedData(true);
                return;
            }
        }

        if (canEditOdometer) {
            if (odometerValue && !/^\d*\.?\d*$/.test(odometerValue)) {
                setShowOdometerError(true);
                return;
            }

            // Verificar contra el odometer de la orden actual o del vehículo como fallback
            const currentOdometerValue = currentOrder?.odometer || vehicleData?.odometer;

            if (odometerValue && currentOdometerValue) {
                const newValue = Number(odometerValue);
                const currentValue = Number(currentOdometerValue);

                if (newValue < currentValue) {
                    setShowOdometerLowerError(true);
                    return;
                }
            }
        }

        const updatedData = {};

        if (canEditAllFields) {
            updatedData.requestingUserId = selectedUserId;
            updatedData.maintenanceTypeId = selectedMaintenanceTypeId;
            updatedData.instructions = instructionsValue;
        }

        if (canEditOdometer) {
            updatedData.odometer = odometerValue ? Number(odometerValue) : null;
        }

        if (Object.keys(updatedData).length === 0) {
            setErrorDuplicatedData('No hay campos editables en el estado actual de la orden');
            setShowErrorDuplicatedData(true);
            return;
        }

        try {
            const response = await editWorkOrder(loggedUser.token, orderId, updatedData, currentOrder);

            if (response.success) {
                if (response.noChanges) {
                    setErrorDuplicatedData('No se detectaron cambios en la orden');
                    setShowErrorDuplicatedData(true);
                    return;
                }

                setCurrentOrder(response.data);

                if (canEditAllFields) {
                    setSelectedUserId(response.data.requesterId);
                    setSelectedMaintenanceTypeId(response.data.maintenanceTypeId);
                    setInstructionsValue(response.data.instructions || '');
                }

                if (canEditOdometer) {
                    setOdometerValue(response.data.odometer != null ? response.data.odometer.toString() : '');
                }

                setShowOrderEdited(true);
            } else {
                setErrorDuplicatedData(response.error || 'Error al editar la orden de trabajo');
                setShowErrorDuplicatedData(true);
            }
        } catch (error) {
            console.error('Error inesperado al editar orden:', error);
            setErrorDuplicatedData('Error inesperado al editar la orden de trabajo');
            setShowErrorDuplicatedData(true);
        }
    };

    const handleOpenDeleteInternalServiceModal = (serviceId) => {
        setDeletingInternalServiceId(serviceId);
        setShowDeleteInternalServiceModal(true);
    };

    const handleCloseDeleteInternalServiceModal = () => {
        setShowDeleteInternalServiceModal(false);
        setDeletingInternalServiceId(null);
    };

    const handleDeleteInternalService = async () => {
        if (!deletingInternalServiceId) return;

        clearMessages();

        try {
            const response = await deleteInternalService(loggedUser.token, orderId, deletingInternalServiceId);

            if (response.success) {
                handleCloseDeleteInternalServiceModal();
                setShowInternalServiceDeleted(true);
                await fetchOrderData();
            } else {
                setErrorDuplicatedData(response.error || 'No se pudo eliminar el servicio interno.');
                setShowErrorDuplicatedData(true);
                handleCloseDeleteInternalServiceModal();
            }
        } catch (error) {
            setErrorDuplicatedData('Error inesperado al eliminar el servicio interno.');
            setShowErrorDuplicatedData(true);
            handleCloseDeleteInternalServiceModal();
        }
    };

    const handleOpenDeleteExternalServiceModal = (serviceId) => {
        setDeletingExternalServiceId(serviceId);
        setShowDeleteExternalServiceModal(true);
    };

    const handleCloseDeleteExternalServiceModal = () => {
        setShowDeleteExternalServiceModal(false);
        setDeletingExternalServiceId(null);
    };

    const handleDeleteExternalService = async () => {
        if (!deletingExternalServiceId) return;

        clearMessages();

        try {
            const response = await deleteExternalService(loggedUser.token, orderId, deletingExternalServiceId);

            if (response.success) {
                handleCloseDeleteExternalServiceModal();
                setShowExternalServiceDeleted(true);
                await fetchOrderData();
            } else {
                setErrorDuplicatedData(response.error || 'No se pudo eliminar el servicio externo.');
                setShowErrorDuplicatedData(true);
                handleCloseDeleteExternalServiceModal();
            }
        } catch (error) {
            setErrorDuplicatedData('Error inesperado al eliminar el servicio externo.');
            setShowErrorDuplicatedData(true);
            handleCloseDeleteExternalServiceModal();
        }
    };

    const handleOpenCreateIssueMovementModal = () => {
        setShowCreateIssueMovementModal(true);
    };

    // Handlers de cambio de estado
    const openStatusConfirmationModal = (newStatus, actionHandler) => {
        setPendingStatusChange({
            newStatus,
            actionHandler,
        });
        setShowStatusModal(true);
    };

    const closeStatusConfirmationModal = () => {
        setShowStatusModal(false);
        setPendingStatusChange(null);
    };

    const executeStatusChange = async () => {
        if (pendingStatusChange?.actionHandler) {
            await pendingStatusChange.actionHandler();
            closeStatusConfirmationModal();
        }
    };

    const handleApproveOrder = async () => {
        clearMessages();
        setIsChangingStatus(true);

        try {
            const response = await approveWorkOrder(loggedUser.token, orderId);

            if (response.success) {
                setCurrentOrder({ ...currentOrder, status: ORDER_STATUS.APPROVED.value });
                setShowStatusChanged(true);
                await fetchOrderData();
            } else {
                setErrorDuplicatedData(response.error || 'Error al aprobar la orden de trabajo');
                setShowErrorDuplicatedData(true);
            }
        } catch (error) {
            console.error('Error inesperado al aprobar orden:', error);
            setErrorDuplicatedData('Error inesperado al procesar la solicitud');
            setShowErrorDuplicatedData(true);
        } finally {
            setIsChangingStatus(false);
        }
    };

    const handleStartMaintenance = async () => {
        clearMessages();
        setIsChangingStatus(true);

        try {
            const response = await startMaintenanceWorkOrder(loggedUser.token, orderId);

            if (response.success) {
                setCurrentOrder({ ...currentOrder, status: ORDER_STATUS.MAINTENANCE.value });
                setShowStatusChanged(true);
                await fetchOrderData();
            } else {
                setErrorDuplicatedData(response.error || 'Error al iniciar el mantenimiento de la orden');
                setShowErrorDuplicatedData(true);
            }
        } catch (error) {
            console.error('Error inesperado al iniciar mantenimiento:', error);
            setErrorDuplicatedData('Error inesperado al procesar la solicitud');
            setShowErrorDuplicatedData(true);
        } finally {
            setIsChangingStatus(false);
        }
    };

    const handleDiscardOrder = async () => {
        clearMessages();
        setIsChangingStatus(true);

        try {
            const response = await discardWorkOrder(loggedUser.token, orderId);

            if (response.success) {
                setCurrentOrder({ ...currentOrder, status: ORDER_STATUS.DISCARDED.value });
                setShowStatusChanged(true);
                await fetchOrderData();
            } else {
                setErrorDuplicatedData(response.error || 'Error al descartar la orden de trabajo');
                setShowErrorDuplicatedData(true);
            }
        } catch (error) {
            console.error('Error inesperado al descartar orden:', error);
            setErrorDuplicatedData('Error inesperado al procesar la solicitud');
            setShowErrorDuplicatedData(true);
        } finally {
            setIsChangingStatus(false);
        }
    };

    const handleFinalizeOrder = async () => {
        clearMessages();
        setIsChangingStatus(true);

        try {
            const response = await finalizeWorkOrder(loggedUser.token, orderId);

            if (response.success) {
                setCurrentOrder({
                    ...currentOrder,
                    status: ORDER_STATUS.FINALIZED.value,
                    maintenanceEndDate: response.data.maintenanceEndDate,
                });
                setShowStatusChanged(true);
                await fetchOrderData();
            } else {
                setErrorDuplicatedData(response.error || 'Error al finalizar la orden de trabajo');
                setShowErrorDuplicatedData(true);
            }
        } catch (error) {
            console.error('Error inesperado al finalizar orden:', error);
            setErrorDuplicatedData('Error inesperado al procesar la solicitud');
            setShowErrorDuplicatedData(true);
        } finally {
            setIsChangingStatus(false);
        }
    };

    const shouldShowTransitionButton = (targetStatus) => {
        if (targetStatus === ORDER_STATUS.DISCARDED.value || targetStatus === ORDER_STATUS.FINALIZED.value) {
            return true;
        }
        return true;
    };

    const shouldDisableTransitionButton = (targetStatus) => {
        if (!currentOrder) return true;

        if (targetStatus === ORDER_STATUS.DISCARDED.value) {
            const hasPerformedWorks = currentOrder.performedWorks && currentOrder.performedWorks.length > 0;
            const hasExternalWorks = currentOrder.externalWorks && currentOrder.externalWorks.length > 0;

            // Validar despachos: no debe haber despachos activos (con valor pendiente)
            const hasActiveDispatches = issueMovements.some((issue) => {
                // Si totalValueWithReturn > 0, significa que hay valor pendiente (no todo fue devuelto)
                return issue.totalValueWithReturn && issue.totalValueWithReturn > 0;
            });

            if (hasPerformedWorks || hasExternalWorks || hasActiveDispatches) {
                return true;
            }
        }

        if (targetStatus === ORDER_STATUS.FINALIZED.value) {
            const hasPerformedWorks = currentOrder.performedWorks && currentOrder.performedWorks.length > 0;
            const hasExternalWorks = currentOrder.externalWorks && currentOrder.externalWorks.length > 0;

            // Verificar si hay despachos activos (con valor pendiente)
            const hasActiveDispatches = issueMovements.some((issue) => {
                return issue.totalValueWithReturn && issue.totalValueWithReturn > 0;
            });

            // La orden debe tener al menos un servicio o despacho activo
            if (!hasPerformedWorks && !hasExternalWorks && !hasActiveDispatches) {
                return true; // No se puede finalizar una orden vacía
            }

            // Validar servicios internos (si existen) - todos deben estar completos
            if (hasPerformedWorks) {
                const hasIncompleteInternalServices = currentOrder.performedWorks.some((work) => {
                    const hasStartButNoEnd = work.startTime && !work.endTime;
                    const noHoursSpent = !work.hoursSpent || work.hoursSpent === 0;
                    const noValidTotalAmount =
                        work.totalAmount === null || work.totalAmount === undefined || typeof work.totalAmount !== 'number' || work.totalAmount <= 0;

                    return hasStartButNoEnd || noHoursSpent || noValidTotalAmount;
                });

                if (hasIncompleteInternalServices) {
                    return true;
                }
            }

            // Validar servicios externos (si existen) - todos deben estar completos
            if (hasExternalWorks) {
                const hasIncompleteExternalServices = currentOrder.externalWorks.some((work) => {
                    const hasStartButNoEnd = work.startTime && !work.endTime;
                    const noHoursSpent = !work.hoursSpent || work.hoursSpent === 0;
                    const noValidTotalAmount =
                        work.totalAmount === null || work.totalAmount === undefined || typeof work.totalAmount !== 'number' || work.totalAmount <= 0;

                    return hasStartButNoEnd || noHoursSpent || noValidTotalAmount;
                });

                if (hasIncompleteExternalServices) {
                    return true;
                }
            }
        }

        return false;
    };

    const renderStatusActionButtons = () => {
        if (!currentOrder) return null;

        const availableTransitions = getAvailableStatusTransitions(currentOrder.status);

        if (availableTransitions.length === 0) {
            return null;
        }

        const visibleTransitions = availableTransitions.filter((transition) => shouldShowTransitionButton(transition.value));

        if (visibleTransitions.length === 0) {
            return null;
        }

        return (
            <StatusActionsContainer>
                {visibleTransitions.map((transition) => {
                    let buttonConfig = {
                        onClick: null,
                        text: '',
                        variant: 'primary',
                        disabledMessage: '',
                    };

                    switch (transition.value) {
                        case ORDER_STATUS.APPROVED.value:
                            buttonConfig = {
                                onClick: () => openStatusConfirmationModal(ORDER_STATUS.APPROVED.value, handleApproveOrder),
                                text: 'Aprobar orden',
                                variant: 'success',
                            };
                            break;
                        case ORDER_STATUS.MAINTENANCE.value:
                            buttonConfig = {
                                onClick: () => openStatusConfirmationModal(ORDER_STATUS.MAINTENANCE.value, handleStartMaintenance),
                                text: 'Iniciar mantenimiento',
                                variant: 'warning',
                            };
                            break;
                        case ORDER_STATUS.FINALIZED.value:
                            buttonConfig = {
                                onClick: () => openStatusConfirmationModal(ORDER_STATUS.FINALIZED.value, handleFinalizeOrder),
                                text: 'Finalizar orden',
                                variant: 'primary',
                                disabledMessage: 'No se permite finalizar si la orden está vacía o tiene servicios incompletos',
                            };
                            break;
                        case ORDER_STATUS.DISCARDED.value:
                            buttonConfig = {
                                onClick: () => openStatusConfirmationModal(ORDER_STATUS.DISCARDED.value, handleDiscardOrder),
                                text: 'Descartar orden',
                                variant: 'danger',
                                disabledMessage: 'No se permite descartar si hay servicios registrados o despachos activos',
                            };
                            break;
                        default:
                            return null;
                    }

                    const isDiscardButton = transition.value === ORDER_STATUS.DISCARDED.value;
                    const isDisabled = shouldDisableTransitionButton(transition.value) || isChangingStatus;
                    const tooltipMessage = isDisabled && !isChangingStatus ? buttonConfig.disabledMessage : '';

                    return (
                        <TooltipWrapper key={transition.value} data-tooltip={tooltipMessage}>
                            <button
                                type='button'
                                className={`btn ${isDiscardButton ? 'btn-outline-danger' : ''}`}
                                style={
                                    !isDiscardButton
                                        ? {
                                              backgroundColor: transition.color,
                                              borderColor: transition.color,
                                              color: '#fff',
                                          }
                                        : {}
                                }
                                onClick={buttonConfig.onClick}
                                disabled={isDisabled}
                            >
                                {isChangingStatus ? (
                                    <>
                                        <span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true'></span>
                                        Procesando...
                                    </>
                                ) : (
                                    buttonConfig.text
                                )}
                            </button>
                        </TooltipWrapper>
                    );
                })}
            </StatusActionsContainer>
        );
    };

    // Handlers para modal de servicios internos
    const handleOpenInternalServiceModal = (service = null) => {
        setEditingInternalService(service);
        setShowInternalServiceModal(true);
    };

    const handleCloseInternalServiceModal = () => {
        setShowInternalServiceModal(false);
        setEditingInternalService(null);
    };

    const handleSaveInternalService = async (formData, setModalApiError) => {
        setIsSubmittingInternalService(true);
        clearMessages();

        try {
            const isEditing = editingInternalService && editingInternalService.id;

            let response;
            if (isEditing) {
                response = await editInternalService(loggedUser.token, orderId, editingInternalService.id, formData);
            } else {
                response = await createInternalService(loggedUser.token, orderId, formData);
            }

            if (response.success) {
                handleCloseInternalServiceModal();

                if (isEditing) {
                    setShowInternalServiceEdited(true);
                } else {
                    setShowInternalServiceCreated(true);
                }

                await fetchOrderData();
            } else {
                const errorMessage = response.error || 'No se pudo guardar el servicio interno. Por favor, verifica los datos e intenta nuevamente.';
                setModalApiError(errorMessage);
            }
        } catch (error) {
            setModalApiError('Error de conexión. Verifica tu internet e intenta nuevamente.');
        } finally {
            setIsSubmittingInternalService(false);
        }
    };

    // Handlers para modal de servicios externos
    const handleOpenExternalServiceModal = (service = null) => {
        setEditingExternalService(service);
        setShowExternalServiceModal(true);
    };

    const handleCloseExternalServiceModal = () => {
        setShowExternalServiceModal(false);
        setEditingExternalService(null);
    };

    const handleSaveExternalService = async (formData, setModalApiError) => {
        setIsSubmittingExternalService(true);
        clearMessages();

        try {
            const isEditing = editingExternalService && editingExternalService.id;

            let response;
            if (isEditing) {
                response = await editExternalService(loggedUser.token, orderId, editingExternalService.id, formData);
            } else {
                response = await createExternalService(loggedUser.token, orderId, formData);
            }

            if (response.success) {
                handleCloseExternalServiceModal();

                if (isEditing) {
                    setShowExternalServiceEdited(true);
                } else {
                    setShowExternalServiceCreated(true);
                }

                await fetchOrderData();
            } else {
                const errorMessage = response.error || 'No se pudo guardar el servicio externo.';
                setModalApiError(errorMessage);
            }
        } catch (error) {
            setModalApiError('Error de conexión. Verifica tu internet e intenta nuevamente.');
        } finally {
            setIsSubmittingExternalService(false);
        }
    };

    // Permission Check
    const hasManagePermission = checkPermission(loggedUser.permissions, 'manage_work_orders');

    // Funciones para calcular totales de servicios
    const calculateInternalServicesTotal = () => {
        return internalServices.reduce((total, service) => {
            const amount = service.totalAmount;
            return total + (amount && typeof amount === 'number' ? amount : 0);
        }, 0);
    };

    const calculateExternalServicesTotal = () => {
        return externalServices.reduce((total, service) => {
            const amount = service.totalAmount;
            return total + (amount && typeof amount === 'number' ? amount : 0);
        }, 0);
    };

    const calculateDispatchesTotal = () => {
        return issueMovements.reduce((total, issue) => {
            const amount = issue.totalValueWithReturn;
            return total + (amount && typeof amount === 'number' ? amount : 0);
        }, 0);
    };

    /**
     * Calcula el valor total de la orden sumando servicios internos, externos y despachos
     */
    const calculateOrderTotal = () => {
        const internalTotal = calculateInternalServicesTotal();
        const externalTotal = calculateExternalServicesTotal();
        const dispatchesTotal = calculateDispatchesTotal();

        return internalTotal + externalTotal + dispatchesTotal;
    };

    // Redirección si no tiene permisos
    useEffect(() => {
        if (!hasManagePermission) {
            navigate('/ordenes');
        }
    }, [hasManagePermission, navigate]);

    if (!hasManagePermission) {
        return null;
    }

    if (isLoading) {
        return (
            <div className='container mt-4'>
                <div className='mb-3'>
                    <Link to='/ordenes'>Volver a gestión de órdenes</Link>
                </div>
                <div className='text-center mt-5'>
                    <div className='spinner-border text-primary' role='status'>
                        <span className='visually-hidden'>Cargando...</span>
                    </div>
                    <p className='mt-3'>Cargando información de la orden...</p>
                </div>
            </div>
        );
    }

    if (loadError) {
        return (
            <div className='container mt-4'>
                <div className='mb-3'>
                    <Link to='/ordenes'>Volver a gestión de órdenes</Link>
                </div>
                <div className='alert alert-danger text-center mt-4' role='alert'>
                    <h4>Error al cargar la orden</h4>
                    <p>{loadError}</p>
                    <button className='btn btn-primary mt-3' onClick={fetchOrderData}>
                        Intentar nuevamente
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className='container mt-4'>
            {showSuccessMessage && (
                <div className={`alert ${isPartialSuccess ? 'alert-warning' : 'alert-success'} text-center mb-1`} role='alert'>
                    {successMessage || 'Orden de trabajo creada exitosamente'}
                </div>
            )}

            {showOrderEdited && (
                <div className='alert alert-success text-center mb-1' role='alert'>
                    Orden de trabajo editada satisfactoriamente.
                </div>
            )}

            {showStatusChanged && (
                <div className='alert alert-success text-center mb-1' role='alert'>
                    Estado de la orden cambiado satisfactoriamente.
                </div>
            )}

            {showInternalServiceEdited && (
                <div className='alert alert-success text-center mb-1' role='alert'>
                    Servicio interno editado satisfactoriamente.
                </div>
            )}

            {showInternalServiceCreated && (
                <div className='alert alert-success text-center mb-1' role='alert'>
                    Servicio interno creado satisfactoriamente.
                </div>
            )}

            {showInternalServiceDeleted && (
                <div className='alert alert-success text-center mb-1' role='alert'>
                    Servicio interno eliminado satisfactoriamente.
                </div>
            )}

            {showExternalServiceCreated && (
                <div className='alert alert-success text-center mb-1' role='alert'>
                    Servicio externo creado satisfactoriamente.
                </div>
            )}

            {showExternalServiceEdited && (
                <div className='alert alert-success text-center mb-1' role='alert'>
                    Servicio externo editado satisfactoriamente.
                </div>
            )}

            {showExternalServiceDeleted && (
                <div className='alert alert-success text-center mb-1' role='alert'>
                    Servicio externo eliminado satisfactoriamente.
                </div>
            )}

            {showMovementEdited && (
                <div className='alert alert-success text-center mb-1' role='alert'>
                    Devolución procesada satisfactoriamente.
                </div>
            )}

            {showErrorDuplicatedData && (
                <div className='alert alert-danger text-center mb-1' role='alert'>
                    {errorDuplicatedData}
                </div>
            )}

            {showIssueMovementCreated && (
                <div className='alert alert-success text-center mb-1' role='alert'>
                    Despacho creado satisfactoriamente.
                </div>
            )}

            <div className='mb-3'>
                <Link to='/ordenes'>Volver a gestión de órdenes</Link>
            </div>

            <HeaderContainer>
                <h1 className='mb-0'>Editar Orden de Trabajo #{currentOrder?.orderNumber || ''}</h1>
                {currentOrder?.status && (
                    <StatusBadge
                        style={{
                            backgroundColor: getOrderStatusConfig(currentOrder.status).color,
                        }}
                    >
                        {getOrderStatusConfig(currentOrder.status).label}
                    </StatusBadge>
                )}
            </HeaderContainer>

            <FormContainer>
                <form onSubmit={handleSubmitMain(handleEditMainData)}>
                    <InfoField>
                        <label className='fw-bold'>Placa:</label>
                        <span>{currentOrder?.vehiclePlate || '-'}</span>
                    </InfoField>

                    <InfoField>
                        <label className='fw-bold'>Tipo de cliente:</label>
                        <span>{currentOrder?.orderClientType ? getClientTypeLabel(currentOrder.orderClientType) : '-'}</span>
                    </InfoField>

                    <InfoField>
                        <label className='fw-bold'>Valor total:</label>
                        <span>₡{formatCurrency(calculateOrderTotal())}</span>
                    </InfoField>

                    <hr />

                    <label className='d-flex mb-1'>Solicitante</label>
                    <Autocomplete
                        disablePortal
                        id='user-select'
                        size='small'
                        options={availableUsers}
                        value={selectedUserId && availableUsers.length > 0 ? availableUsers.find((user) => user.id === selectedUserId) || null : null}
                        renderInput={(params) => (
                            <TextField {...params} label={selectedUserId ? '' : 'Buscar por nombre, apellido o usuario'} error={false} />
                        )}
                        onChange={handleSelectUser}
                        onInputChange={handleSearchUsers}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        noOptionsText='Buscar por nombre, apellido o usuario'
                        disabled={!canEditAllFields}
                        sx={autocompleteIconStyles}
                    />
                    <span className='my-1' role='alert'>
                        {showUserError && 'Campo requerido'}
                    </span>

                    <label className='d-flex mb-1'>Tipo de mantenimiento</label>
                    <Autocomplete
                        disablePortal
                        id='maintenance-type-select'
                        size='small'
                        options={maintenanceTypes.map((type) => ({
                            id: type.id,
                            label: type.name,
                        }))}
                        value={
                            selectedMaintenanceTypeId && maintenanceTypes.length > 0
                                ? maintenanceTypes
                                      .map((type) => ({
                                          id: type.id,
                                          label: type.name,
                                      }))
                                      .find((type) => type.id === selectedMaintenanceTypeId) || null
                                : null
                        }
                        renderInput={(params) => (
                            <TextField {...params} label={selectedMaintenanceTypeId ? '' : 'Seleccionar tipo de mantenimiento'} error={false} />
                        )}
                        onChange={handleSelectMaintenanceType}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        noOptionsText='No hay tipos de mantenimiento disponibles'
                        disabled={!canEditAllFields}
                        sx={autocompleteIconStyles}
                    />
                    <span className='my-1' role='alert'>
                        {showMaintenanceTypeError && 'Campo requerido'}
                    </span>

                    <label className='d-flex mb-1' htmlFor='odometer'>
                        Kilometraje
                    </label>
                    <input
                        className='form-control'
                        type='text'
                        id='odometer'
                        autoComplete='off'
                        maxLength={10}
                        value={odometerValue}
                        onChange={handleOdometerChange}
                        disabled={!canEditOdometer}
                        pattern='^\d*\.?\d*$'
                    />
                    <span
                        className={`my-1 ${
                            !showOdometerError && !showOdometerLowerError && !isLoadingVehicle && vehicleData && !currentOrder?.odometer ? 'info' : ''
                        }`}
                        role='alert'
                    >
                        {showOdometerError && 'Solo se permiten números'}
                        {!showOdometerError &&
                            showOdometerLowerError &&
                            `El kilometraje no puede ser menor al actual (${currentOrder?.odometer || vehicleData?.odometer || 0})`}
                        {!showOdometerError && !showOdometerLowerError && isLoadingVehicle && 'Cargando datos del vehículo...'}
                        {!showOdometerError &&
                            !showOdometerLowerError &&
                            !isLoadingVehicle &&
                            vehicleData &&
                            !currentOrder?.odometer &&
                            `Kilometraje actual del vehículo: ${vehicleData.odometer ? vehicleData.odometer : '0'} km`}
                    </span>

                    <label className='d-flex mb-1' htmlFor='instructions'>
                        Instrucciones
                    </label>
                    <textarea
                        className='form-control'
                        id='instructions'
                        rows={3}
                        maxLength={500}
                        placeholder='Estas instrucciones se le mostrarán al creador de la orden'
                        value={instructionsValue}
                        onChange={handleInstructionsChange}
                        disabled={!canEditAllFields}
                    />
                    <span className='my-1' role='alert'>
                        {instructionsValue.length > 500 && 'Máximo 500 caracteres'}
                    </span>

                    <ActionButtonsContainer>
                        {(canEditAllFields || canEditOnlyOdometer) && (
                            <button className='btn btn-danger' type='submit'>
                                Guardar cambios
                            </button>
                        )}

                        {renderStatusActionButtons()}
                    </ActionButtonsContainer>
                </form>
            </FormContainer>

            <hr />

            {/* ACORDEÓN DE SECCIONES */}
            <AccordionWrapper activeKey={activeKey} onSelect={(k) => setActiveKey(k)}>
                {/* SERVICIOS INTERNOS */}
                <Accordion.Item eventKey='0'>
                    <Accordion.Header>
                        <AccordionTitleContainer>
                            <AccordionTitle>Servicios Internos</AccordionTitle>
                            {internalServices.length > 0 && <AccordionTotal>(₡{formatCurrency(calculateInternalServicesTotal())})</AccordionTotal>}
                        </AccordionTitleContainer>
                    </Accordion.Header>
                    <Accordion.Body>
                        <ServicesContainer>
                            {currentOrder?.status === ORDER_STATUS.MAINTENANCE.value && (
                                <AddButtonContainer>
                                    <AddButton type='button' className='btn btn-primary' onClick={() => handleOpenInternalServiceModal()}>
                                        <span className='me-2'>+</span>
                                        Agregar servicio
                                    </AddButton>
                                </AddButtonContainer>
                            )}
                            {internalServices.length > 0 ? (
                                <table className='table table-bordered'>
                                    <thead>
                                        <tr>
                                            <th scope='col'>Tipo</th>
                                            <th scope='col'>Subtipo</th>
                                            <th scope='col'>Descripción</th>
                                            <th scope='col'>Mecánico</th>
                                            <th scope='col'>Inicio</th>
                                            <th scope='col'>Fin</th>
                                            <th scope='col'>Horas</th>
                                            <th scope='col'>Monto total</th>
                                            {currentOrder?.status === ORDER_STATUS.MAINTENANCE.value && <th scope='col'>Acciones</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {internalServices.map((service) => (
                                            <tr key={service.id}>
                                                <td>{service.workTypeName}</td>
                                                <td>{service.workSubtypeName}</td>
                                                <td>
                                                    <DescriptionCell data-tooltip={service.description || ''}>
                                                        {service.description
                                                            ? service.description.length > 30
                                                                ? `${service.description.substring(0, 30)}...`
                                                                : service.description
                                                            : '-'}
                                                    </DescriptionCell>
                                                </td>
                                                <td>{service.mechanicName}</td>
                                                <td>{formatDateOnly(service.startTime)}</td>
                                                <td>{service.endTime ? formatDateOnly(service.endTime) : 'En proceso'}</td>
                                                <td>{formatHours(service.hoursSpent)}</td>
                                                <td>
                                                    {service.totalAmount !== null && service.totalAmount !== undefined
                                                        ? `₡${formatCurrency(service.totalAmount)}`
                                                        : '-'}
                                                </td>
                                                {currentOrder?.status === ORDER_STATUS.MAINTENANCE.value && (
                                                    <td>
                                                        <div className='d-flex'>
                                                            <TableActionButton
                                                                type='button'
                                                                className='btn btn-primary'
                                                                onClick={() => handleOpenInternalServiceModal(service)}
                                                            >
                                                                <MdModeEdit />
                                                                Editar
                                                            </TableActionButton>
                                                            <TableActionButton
                                                                type='button'
                                                                className='btn btn-danger'
                                                                onClick={() => handleOpenDeleteInternalServiceModal(service.id)}
                                                            >
                                                                <MdDelete />
                                                                Eliminar
                                                            </TableActionButton>
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <p className='text-muted'>No hay servicios internos registrados para esta orden.</p>
                            )}
                        </ServicesContainer>
                    </Accordion.Body>
                </Accordion.Item>

                {/* SERVICIOS EXTERNOS */}
                <Accordion.Item eventKey='1'>
                    <Accordion.Header>
                        <AccordionTitleContainer>
                            <AccordionTitle>Servicios Externos</AccordionTitle>
                            {externalServices.length > 0 && <AccordionTotal>(₡{formatCurrency(calculateExternalServicesTotal())})</AccordionTotal>}
                        </AccordionTitleContainer>
                    </Accordion.Header>
                    <Accordion.Body>
                        <ServicesContainer>
                            {currentOrder?.status === ORDER_STATUS.MAINTENANCE.value && (
                                <AddButtonContainer>
                                    <AddButton type='button' className='btn btn-primary' onClick={() => handleOpenExternalServiceModal()}>
                                        <span className='me-2'>+</span>
                                        Agregar servicio
                                    </AddButton>
                                </AddButtonContainer>
                            )}
                            {externalServices.length > 0 ? (
                                <table className='table table-bordered'>
                                    <thead>
                                        <tr>
                                            <th scope='col'>Tipo</th>
                                            <th scope='col'>Subtipo</th>
                                            <th scope='col'>Descripción</th>
                                            <th scope='col'>Proveedor</th>
                                            <th scope='col'>Inicio</th>
                                            <th scope='col'>Fin</th>
                                            <th scope='col'>Horas</th>
                                            <th scope='col'>Monto total</th>
                                            {currentOrder?.status === ORDER_STATUS.MAINTENANCE.value && <th scope='col'>Acciones</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {externalServices.map((service) => (
                                            <tr key={service.id}>
                                                <td>{service.workTypeName}</td>
                                                <td>{service.workSubtypeName}</td>
                                                <td>
                                                    <DescriptionCell data-tooltip={service.description || ''}>
                                                        {service.description
                                                            ? service.description.length > 30
                                                                ? `${service.description.substring(0, 30)}...`
                                                                : service.description
                                                            : '-'}
                                                    </DescriptionCell>
                                                </td>
                                                <td>{service.mechanicName}</td>
                                                <td>{formatDateOnly(service.startTime)}</td>
                                                <td>{service.endTime ? formatDateOnly(service.endTime) : 'En proceso'}</td>
                                                <td>{formatHours(service.hoursSpent)}</td>
                                                <td>
                                                    {service.totalAmount !== null && service.totalAmount !== undefined
                                                        ? `₡${formatCurrency(service.totalAmount)}`
                                                        : '-'}
                                                </td>
                                                {currentOrder?.status === ORDER_STATUS.MAINTENANCE.value && (
                                                    <td>
                                                        <div className='d-flex'>
                                                            <TableActionButton
                                                                type='button'
                                                                className='btn btn-primary'
                                                                onClick={() => handleOpenExternalServiceModal(service)}
                                                            >
                                                                <MdModeEdit />
                                                                Editar
                                                            </TableActionButton>
                                                            <TableActionButton
                                                                type='button'
                                                                className='btn btn-danger'
                                                                onClick={() => handleOpenDeleteExternalServiceModal(service.id)}
                                                            >
                                                                <MdDelete />
                                                                Eliminar
                                                            </TableActionButton>
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <p className='text-muted'>No hay servicios externos registrados para esta orden.</p>
                            )}
                        </ServicesContainer>
                    </Accordion.Body>
                </Accordion.Item>

                {/* SECCIÓN DE DESPACHOS */}
                <Accordion.Item eventKey='3'>
                    <Accordion.Header>
                        <AccordionTitleContainer>
                            <AccordionTitle>Despachos</AccordionTitle>
                            {issueMovements.length > 0 && <AccordionTotal>(₡{formatCurrency(calculateDispatchesTotal())})</AccordionTotal>}
                        </AccordionTitleContainer>
                    </Accordion.Header>
                    <Accordion.Body>
                        <DispatchesContainer>
                            {currentOrder?.status === ORDER_STATUS.MAINTENANCE.value && (
                                <AddButtonContainer>
                                    <AddButton type='button' className='btn btn-primary' onClick={() => handleOpenCreateIssueMovementModal()}>
                                        <span className='me-2'>+</span>
                                        Agregar despacho
                                    </AddButton>
                                </AddButtonContainer>
                            )}

                            {isLoadingIssues ? (
                                <div className='text-center py-4'>
                                    <div className='spinner-border text-primary' role='status'>
                                        <span className='visually-hidden'>Cargando despachos...</span>
                                    </div>
                                </div>
                            ) : issueMovements.length > 0 ? (
                                <Accordion alwaysOpen defaultActiveKey={issueMovements.length > 0 ? `dispatch-${issueMovements[0].id}` : undefined}>
                                    {issueMovements.map((issue, index) => {
                                        const dispatchNumber = index + 1;
                                        const movementDetails = issueMovementDetails[issue.id];
                                        const isLoadingDetails = loadingIssueDetails[issue.id];

                                        return (
                                            <Accordion.Item eventKey={`dispatch-${issue.id}`} key={issue.id}>
                                                <Accordion.Header onClick={() => fetchIssueMovementDetails(issue.id)}>
                                                    <div className='d-flex justify-content-between align-items-center w-100 me-3'>
                                                        <div className='flex-grow-1'>
                                                            <DispatchTitle>Despacho #{dispatchNumber}</DispatchTitle>
                                                            <div className='row'>
                                                                <div className='col-md-3 mb-1'>
                                                                    <small>
                                                                        <strong>Fecha:</strong>{' '}
                                                                        {issue.eventDate ? formatDateAndTime(issue.eventDate) : '--'}
                                                                    </small>
                                                                </div>
                                                                <div className='col-md-3 mb-1'>
                                                                    <small>
                                                                        <strong>Despachador:</strong> {issue.user}
                                                                    </small>
                                                                </div>
                                                                <div className='col-md-3 mb-1'>
                                                                    <small>
                                                                        <strong>Solicitante:</strong> {issue.receivingUser}
                                                                    </small>
                                                                </div>
                                                                <div className='col-md-3 mb-1'>
                                                                    <small>
                                                                        <strong>Valor total:</strong> ₡{formatCurrency(issue.totalValueWithReturn)}
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className='ms-2 d-flex gap-2'>
                                                            <TableActionButton
                                                                type='button'
                                                                className='btn btn-primary'
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleShowIssueDetails(issue.id);
                                                                }}
                                                            >
                                                                <FaMagnifyingGlass />
                                                                Detalles
                                                            </TableActionButton>

                                                            {currentOrder?.status === ORDER_STATUS.MAINTENANCE.value && (
                                                                <OverlayTrigger
                                                                    delay={{ hide: 200, show: 300 }}
                                                                    overlay={(props) => (
                                                                        <Tooltip {...props}>
                                                                            {issue.returnMovement === null
                                                                                ? 'Hacer una devolución'
                                                                                : 'Editar la devolución'}
                                                                        </Tooltip>
                                                                    )}
                                                                    placement='top'
                                                                >
                                                                    <TableActionButton
                                                                        type='button'
                                                                        className='btn btn-primary'
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            issue.returnMovement === null
                                                                                ? handleShowReturnModal(issue.id)
                                                                                : handleShowEditReturnModal(issue.returnMovement);
                                                                        }}
                                                                    >
                                                                        {issue.returnMovement === null ? <GrRevert /> : <MdModeEdit />}
                                                                        Editar
                                                                    </TableActionButton>
                                                                </OverlayTrigger>
                                                            )}
                                                        </div>
                                                    </div>
                                                </Accordion.Header>
                                                <Accordion.Body>
                                                    <div className='px-3 py-2'>
                                                        {/* Descripción si existe */}
                                                        {issue.description && (
                                                            <div className='mb-3'>
                                                                <strong>Descripción:</strong> {issue.description}
                                                            </div>
                                                        )}

                                                        <hr />

                                                        {/* Artículos del despacho */}
                                                        {isLoadingDetails ? (
                                                            <div className='text-center py-3'>
                                                                <div className='spinner-border spinner-border-sm text-primary' role='status'>
                                                                    <span className='visually-hidden'>Cargando detalles...</span>
                                                                </div>
                                                                <p className='mt-2 mb-0 text-muted'>Cargando artículos del despacho...</p>
                                                            </div>
                                                        ) : movementDetails && movementDetails.details ? (
                                                            <>
                                                                <h6 className='fw-bold mb-3'>Artículos despachados:</h6>
                                                                <div className='table-responsive'>
                                                                    <table className='table table-bordered table-sm'>
                                                                        <thead className='table-light'>
                                                                            <tr>
                                                                                <th scope='col'>Artículo</th>
                                                                                <th scope='col'>Marca</th>
                                                                                <th scope='col'>Cantidad</th>
                                                                                <th scope='col'>Precio unitario</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {movementDetails.details.map((item, itemIndex) => (
                                                                                <tr key={itemIndex}>
                                                                                    <td>{item.isService ? item.serviceConcept : item.articleName}</td>
                                                                                    <td>{item.isService ? '--' : item.brand}</td>
                                                                                    <td>{item.amount}</td>
                                                                                    <td>₡{formatCurrency(item.itemsValue)}</td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </table>
                                                                </div>

                                                                {/* Valor total del despacho */}
                                                                <div className='text-end mb-3'>
                                                                    <div className='d-inline-block p-2 rounded border'>
                                                                        <strong>
                                                                            Valor total del despacho: ₡{formatCurrency(movementDetails.totalValue)}
                                                                        </strong>
                                                                    </div>
                                                                </div>

                                                                {/* Mostrar devolución si existe */}
                                                                {movementDetails.returnMovementDetails && (
                                                                    <>
                                                                        <hr />
                                                                        <h6 className='fw-bold mb-3'>Artículos devueltos:</h6>
                                                                        <div className='table-responsive'>
                                                                            <table className='table table-bordered table-sm'>
                                                                                <thead className='table-light'>
                                                                                    <tr>
                                                                                        <th scope='col'>Artículo</th>
                                                                                        <th scope='col'>Marca</th>
                                                                                        <th scope='col'>Cantidad</th>
                                                                                        <th scope='col'>Precio unitario</th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    {movementDetails.returnMovementDetails.details.map(
                                                                                        (item, itemIndex) => (
                                                                                            <tr key={itemIndex}>
                                                                                                <td>
                                                                                                    {item.isService
                                                                                                        ? item.serviceConcept
                                                                                                        : item.articleName}
                                                                                                </td>
                                                                                                <td>{item.isService ? '--' : item.brand}</td>
                                                                                                <td>{item.amount}</td>
                                                                                                <td>₡{formatCurrency(item.itemsValue)}</td>
                                                                                            </tr>
                                                                                        )
                                                                                    )}
                                                                                </tbody>
                                                                            </table>
                                                                        </div>

                                                                        {/* Valor total de la devolución */}
                                                                        <div className='text-end mb-3'>
                                                                            <div className='d-inline-block p-2 rounded border'>
                                                                                <strong>
                                                                                    Valor total de la devolución: ₡
                                                                                    {formatCurrency(movementDetails.returnMovementDetails.totalValue)}
                                                                                </strong>
                                                                            </div>
                                                                        </div>

                                                                        <hr />

                                                                        {/* Valor total final */}
                                                                        <div className='text-end mb-3'>
                                                                            <div className='d-inline-block p-2 rounded border'>
                                                                                <strong>
                                                                                    Valor total final: ₡
                                                                                    {formatCurrency(
                                                                                        movementDetails.totalValue -
                                                                                            movementDetails.returnMovementDetails.totalValue
                                                                                    )}
                                                                                </strong>
                                                                            </div>
                                                                        </div>
                                                                    </>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <div className='text-center py-3'>
                                                                <p className='text-muted mb-2'>No se pudieron cargar los detalles del despacho.</p>
                                                                <button
                                                                    className='btn btn-sm btn-outline-primary'
                                                                    onClick={() => fetchIssueMovementDetails(issue.id)}
                                                                >
                                                                    Reintentar
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </Accordion.Body>
                                            </Accordion.Item>
                                        );
                                    })}
                                </Accordion>
                            ) : (
                                <p className='text-muted'>No hay despachos registrados para esta orden de trabajo.</p>
                            )}
                        </DispatchesContainer>
                    </Accordion.Body>
                </Accordion.Item>
            </AccordionWrapper>

            {/* Modales */}
            <ChangeStatusConfirmationModal
                show={showStatusModal}
                closeHandler={closeStatusConfirmationModal}
                actionHandler={executeStatusChange}
                isSubmitting={isChangingStatus}
                data={{
                    orderNumber: currentOrder?.orderNumber || '',
                    vehiclePlate: currentOrder?.vehiclePlate || '',
                    currentStatus: currentOrder?.status || '',
                    newStatus: pendingStatusChange?.newStatus || '',
                }}
            />

            <InternalServiceModal
                show={showInternalServiceModal}
                onHide={handleCloseInternalServiceModal}
                service={editingInternalService}
                onSave={handleSaveInternalService}
                isSubmitting={isSubmittingInternalService}
                maintenanceStartDate={currentOrder?.maintenanceStartDate}
            />

            <ExternalServiceModal
                show={showExternalServiceModal}
                onHide={handleCloseExternalServiceModal}
                service={editingExternalService}
                onSave={handleSaveExternalService}
                isSubmitting={isSubmittingExternalService}
                maintenanceStartDate={currentOrder?.maintenanceStartDate}
            />

            <DeleteConfirmationModal
                text={'¿Desea eliminar el servicio interno?'}
                closeHandler={handleCloseDeleteInternalServiceModal}
                actionHandler={handleDeleteInternalService}
                show={showDeleteInternalServiceModal}
            />

            <DeleteConfirmationModal
                text={'¿Desea eliminar el servicio externo?'}
                closeHandler={handleCloseDeleteExternalServiceModal}
                actionHandler={handleDeleteExternalService}
                show={showDeleteExternalServiceModal}
            />

            {showIssueDetailsModal && (
                <StockMovementDetailsModal movementId={selectedIssueId} closeHandler={handleCloseIssueDetailsModal} show={showIssueDetailsModal} />
            )}

            {showCreateIssueMovementModal && (
                <CreateIssueMovementModal
                    show={showCreateIssueMovementModal}
                    onHide={() => setShowCreateIssueMovementModal(false)}
                    onSuccess={handleIssueMovementCreated}
                    workOrderId={orderId}
                />
            )}

            {showReturnModal && (
                <MovementReturnModal
                    movementId={selectedIssueId}
                    closeHandler={handleCloseReturnModal}
                    actionHandler={handleReturn}
                    show={showReturnModal}
                />
            )}

            {showEditReturnModal && (
                <EditMovementReturnModal
                    returnId={selectedReturnId}
                    closeHandler={handleCloseEditReturnModal}
                    actionHandler={handleEditReturn}
                    show={showEditReturnModal}
                />
            )}
        </div>
    );
};

// Styled Components
const HeaderContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 30px;

    h1 {
        margin: 0;
    }
`;

const StatusBadge = styled.span`
    padding: 8px 16px;
    border-radius: 50px;
    color: #fff;
    font-weight: 500;
    font-size: 14px;
    white-space: nowrap;
`;

const FormContainer = styled.div`
    margin: 25px auto 0;
    padding: 40px 40px 20px;
    width: 70%;

    form {
        display: grid;

        > label {
            font-size: 15px;
            font-weight: bold;
        }

        > span {
            color: #d04437;
            font-size: 13px;
            min-height: 12px;

            &.info {
                color: green;
                font-weight: 500;
            }
        }
    }

    .MuiAutocomplete-root {
        width: 100%;
    }
`;

const InfoField = styled.div`
    display: flex;
    gap: 8px;
    margin-bottom: 15px;

    label {
        font-size: 15px;
        margin: 0;
    }

    span {
        font-size: 15px;
        color: #495057;
    }
`;

const ActionButtonsContainer = styled.div`
    display: flex;
    justify-content: center;
    gap: 10px;
    margin-top: 30px;
    flex-wrap: wrap;

    .btn {
        min-width: 180px;
    }
`;

const StatusActionsContainer = styled.div`
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
`;

const ServicesContainer = styled.div`
    padding: 15px;

    p.text-muted {
        font-style: italic;
        color: #6c757d;
    }
`;

const DispatchesContainer = styled.div`
    padding: 15px;

    p.text-muted {
        font-style: italic;
        color: #6c757d;
    }
`;

const DescriptionCell = styled.div`
    max-width: 150px;
    position: relative;
    cursor: pointer;

    &[data-tooltip]:not([data-tooltip='']):hover::before {
        content: attr(data-tooltip);
        position: absolute;
        bottom: 125%;
        left: 50%;
        transform: translateX(-50%);
        background-color: #333;
        color: #fff;
        padding: 8px 12px;
        border-radius: 6px;
        white-space: normal;
        width: max-content;
        max-width: 300px;
        font-size: 13px;
        font-weight: normal;
        line-height: 1.4;
        z-index: 1000;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    }

    &[data-tooltip]:not([data-tooltip='']):hover::after {
        content: '';
        position: absolute;
        bottom: 115%;
        left: 50%;
        transform: translateX(-50%);
        border: 6px solid transparent;
        border-top-color: #333;
        z-index: 1000;
    }
`;

const autocompleteIconStyles = {
    '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
        outline: 'none',
        backgroundColor: 'transparent',
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
        '&:focus': {
            outline: 'none',
            backgroundColor: 'transparent',
        },
        '&:focus-visible': {
            outline: 'none',
            backgroundColor: 'transparent',
        },
    },
};

const TooltipWrapper = styled.span`
    position: relative;
    display: inline-block;
    pointer-events: auto;

    &[data-tooltip]:not([data-tooltip='']):hover::before {
        content: attr(data-tooltip);
        position: absolute;
        bottom: 125%;
        left: 50%;
        transform: translateX(-50%);
        background-color: #333;
        color: #fff;
        padding: 8px 12px;
        border-radius: 6px;
        white-space: normal;
        width: max-content;
        max-width: 300px;
        font-size: 13px;
        font-weight: normal;
        line-height: 1.4;
        z-index: 1000;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    }

    &[data-tooltip]:not([data-tooltip='']):hover::after {
        content: '';
        position: absolute;
        bottom: 115%;
        left: 50%;
        transform: translateX(-50%);
        border: 6px solid transparent;
        border-top-color: #333;
        z-index: 1000;
    }

    & button:disabled {
        pointer-events: none;
        cursor: not-allowed;
    }

    &[data-tooltip]:not([data-tooltip='']) {
        &:has(button:disabled) {
            cursor: not-allowed;
        }
    }
`;

const AccordionWrapper = styled(Accordion)`
    margin-top: 20px;

    .accordion-item {
        border: 1px solid #dee2e6;
        margin-bottom: 10px;
        border-radius: 4px;
    }

    .accordion-button {
        font-size: 16px;
        font-weight: 500;
        padding: 15px 20px;

        &:not(.collapsed) {
            background-color: #f8f9fa;
            color: #000;
            box-shadow: none;
        }

        &:focus {
            box-shadow: none;
            border-color: #dee2e6;
        }
    }

    .accordion-body {
        padding: 0;
    }
`;

const AccordionTitleContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 15px;
    width: 100%;
`;

const AccordionTitle = styled.span`
    font-size: 24px;
    font-weight: 600;
`;

const AccordionTotal = styled.span`
    font-size: 18px;
    font-weight: 500;
    color: #495057;
`;

const AddButtonContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-bottom: 20px;
`;

const DispatchTitle = styled.h6`
    font-size: 1.1rem !important;
    margin-bottom: 0.5rem;
    font-weight: bold;
`;

export default EditOrder;
